import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import colors from 'theme/_colors.scss';
import { InfoMessage, PopOutChart } from 'sharedComponents';
import { chartPlugins, RETRAINING_STATUS } from './constants';
import useMotorAnomalyChartConfig from './useChartConfig';
import useChartResize from 'sharedHooks/useChartResize';
import useUserCapability from 'sharedHooks/useUserCapability';
import {
    EnableUniqueScaling,
    PopOutButton,
    ResetZoomButton,
    withLeftButtonGroup,
    withPopOutRow,
    withRightButtonGroup
} from 'sharedComponents/Chart/headers';
import { getValueType } from '../../KPITrendChartList/helpers';
import { translate } from 'helpers/translateHelper';
import { IconCheckMark } from 'svgIcons/MotionPortalIcons';
import { chartHeightConstants } from '../../constants';
import { calculateDynamicChartHeight } from '../../helpers';
import RetrainingComponent, { RetrainingBanner } from './RetrainingComponent';
import ChartContextMenuButton from './ChartContextMenuButton';
import MotorAssetStatusIndicator from './MotorAssetStatusIndicator';
import './style.scss';


const MotorPopOutChartHeader = ({
    retrainingDetails,
    isPrivilegedUser,
    setShowProgressModal,
    CustomHeader,
    headerProps
}) =>
    <div className='anomaly-chart-header-wrapper'>
        <CustomHeader {...headerProps} />
        <RetrainingBanner
            retrainingDetails={retrainingDetails}
            isPrivilegedUser={isPrivilegedUser}
            onViewProgress={() => setShowProgressModal(true)}
        />
    </div>
    ;

MotorPopOutChartHeader.propTypes = {
    retrainingDetails: PropTypes.object,
    isPrivilegedUser: PropTypes.bool,
    setShowProgressModal: PropTypes.func.isRequired,
    CustomHeader: PropTypes.elementType.isRequired,
    headerProps: PropTypes.object
};


const MotorAnomalyDetectionComponent = ({
    anomalyDetectionKPI,
    loadingStatus,
    assetDetails,
    title,
    levelLabel,
    isZoomed,
    actions,
    selectedAsset
}) => {
    const containerRef = useRef();
    const chartRef = useRef();
    useChartResize(containerRef, chartRef);
    const [chartHeight, setChartHeight] = useState(null);
    const [retrainingModel, setRetrainingModel] = useState(false);
    const [retrainingStartDate, setRetrainingStartDate] = useState(null);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [chartInstance, setChartInstance] = useState(null);
    const exportMenuRef = useRef(null);
    const userCapabilityHelper = useUserCapability();
    const canRetrain = userCapabilityHelper.hasMotorAnomalyRetraining();

    const retrainingDetails = anomalyDetectionKPI?.retrainingDetails;
    const retrainingStatus = retrainingDetails?.retrainingStatus;
    const isRetraining = retrainingStatus === RETRAINING_STATUS.TRAINING;
    const chartRetrainingStartDate = retrainingDetails?.retrainingStartDate;
    const chartRequestedSubmittedDate = retrainingDetails?.requestedSubmittedDate;
    const chartRetrainingCompletedDate = retrainingDetails?.retrainingCompletedDate;

    const graphOptions = useMotorAnomalyChartConfig(
        anomalyDetectionKPI,
        actions?.removeSelectedKpiLegend,
        anomalyDetectionKPI?.name,
        assetDetails,
        translate(getValueType(anomalyDetectionKPI?.valueType?.toLowerCase())),
        anomalyDetectionKPI?.conditionTrends,
        {
            containerRef,
            chartInstance,
            chartHeight,
            retrainingStartDate: chartRetrainingStartDate,
            requestedSubmittedDate: chartRequestedSubmittedDate,
            retrainingCompletedDate: chartRetrainingCompletedDate,
            retrainingStatus
        }
    );

    const updateChartHeight = () => {
        if (chartRef?.current?.chart && containerRef?.current) {
            const containerHeight = containerRef?.current?.clientHeight || chartHeightConstants.default;
            const finalHeight = calculateDynamicChartHeight(
                anomalyDetectionKPI?.conditionTrends,
                false, // always false for motor anomaly
                containerHeight
            );
            chartRef.current.chart.update({ chart: { height: finalHeight } }, true, true);
            setChartHeight(finalHeight);
        }
    };

    useEffect(() => {
        if (!exportMenuOpen) { return; }
        const closeMenu = (e) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
                setExportMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', closeMenu);
        return () => document.removeEventListener('mousedown', closeMenu);
    }, [exportMenuOpen]);

    useEffect(() => {
        window.addEventListener('resize', updateChartHeight);
        return () => window.removeEventListener('resize', updateChartHeight);
    }, []);

    const getAnomalyOverallLevel = () => {
        const kpi = selectedAsset?.trend?.KPIs?.find(k => k?.isAnomaly);
        return kpi?.anomalyLevel ?? 0;
    };

    const motorStatusIndicatorWithProps = useCallback((headerProps) =>
        <MotorAssetStatusIndicator
            {...headerProps}
            anomalyDetectionKPI={anomalyDetectionKPI}
            title={title}
            levelLabel={levelLabel}
            isRetraining={isRetraining}
            isPrivilegedUser={canRetrain}
            setRetrainingModel={setRetrainingModel}
            getAnomalyOverallLevel={getAnomalyOverallLevel}
        />
        , [anomalyDetectionKPI, title, levelLabel, isRetraining, canRetrain, setRetrainingModel, getAnomalyOverallLevel]);

    const chartContextMenuButtonWithProps = useCallback((headerProps) =>
        <ChartContextMenuButton
            {...headerProps}
            chartRef={chartRef}
            exportMenuRef={exportMenuRef}
            exportMenuOpen={exportMenuOpen}
            setExportMenuOpen={setExportMenuOpen}
        />
        , [chartRef, exportMenuRef, exportMenuOpen, setExportMenuOpen]);

    const CustomHeader = useMemo(() => withPopOutRow([
        withLeftButtonGroup([motorStatusIndicatorWithProps, EnableUniqueScaling]),
        withRightButtonGroup([ResetZoomButton, PopOutButton, chartContextMenuButtonWithProps])
    ]), [motorStatusIndicatorWithProps, chartContextMenuButtonWithProps]);

    const motorPopOutChartHeaderWithProps = useCallback((headerProps) =>
        <MotorPopOutChartHeader
            headerProps={headerProps}
            retrainingDetails={retrainingDetails}
            isPrivilegedUser={canRetrain}
            setShowProgressModal={setShowProgressModal}
            CustomHeader={CustomHeader}
        />
        , [retrainingDetails, canRetrain, setShowProgressModal, CustomHeader]);

    if (!anomalyDetectionKPI?.conditionTrends?.length) {
        return (
            <div className='anomaly-detect-empty-message'>
                <IconCheckMark width={'32px'} height={'32px'} color={colors.nero} />
                <span>{translate('ABB.Powertrain.Frontend.anomalyDetectEmptyLabel')}</span>
            </div>
        );
    }

    return (
        <div className='anomaly-detection-wrapper motor-anomaly-detection-wrapper'>
            <div className='anomaly-detection-container' ref={containerRef}>
                <PopOutChart
                    hasKpi={!!anomalyDetectionKPI?.key}
                    renderNoKpi={() => <InfoMessage
                        title='ABB.Powertrain.Frontend.chartNoKPIsSelected'
                        text='ABB.Powertrain.Frontend.chartNoKPIsSelectedUserGuide' />}
                    hasData={!!anomalyDetectionKPI?.conditionTrends?.length}
                    renderNoData={() => <InfoMessage
                        title='ABB.Powertrain.Frontend.chartNoMeasurementData'
                        text='ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuide' />}
                    enableUniqueScaling={false}
                    isZoomed={isZoomed}
                    resetServerZoom={() => actions?.reloadConditionInsights?.(assetDetails?.id)}
                    setIsZoomed={actions?.setIsZoomed}
                    loadingStatus={loadingStatus}
                    options={graphOptions}
                    modules={chartPlugins}
                    container={'motor-anomaly-detection-chart'}
                    popOutClassName='motor-anomaly-detection-popout'
                    mainChartHeader={CustomHeader}
                    popOutChartHeader={motorPopOutChartHeaderWithProps}
                    chartRef={chartRef}
                    onChartReady={setChartInstance}
                    chartInstance={chartInstance}
                    mainChartBanner={
                        <RetrainingBanner
                            retrainingDetails={retrainingDetails}
                            isPrivilegedUser={canRetrain}
                            onViewProgress={() => setShowProgressModal(true)}
                        />
                    }
                />
            </div>
            <RetrainingComponent
                retrainingModel={retrainingModel}
                setRetrainingModel={setRetrainingModel}
                actions={actions}
                selectedAsset={selectedAsset}
                retrainingStartDate={retrainingStartDate}
                setRetrainingStartDate={setRetrainingStartDate}
                showProgressModal={showProgressModal}
                setShowProgressModal={setShowProgressModal}
                retrainingDetails={retrainingDetails}
            />
        </div>
    );
};

MotorAnomalyDetectionComponent.propTypes = {
    anomalyDetectionKPI: PropTypes.shape({
        key: PropTypes.string,
        name: PropTypes.string,
        unitGroupId: PropTypes.number,
        valueType: PropTypes.string,
        conditionTrends: PropTypes.array,
        condition: PropTypes.string,
        currentCondition: PropTypes.string,
        retrainingDetails: PropTypes.shape({
            enableRetraining: PropTypes.bool,
            retrainingStatus: PropTypes.string,
            retrainingStartDate: PropTypes.string,
            retrainingCompletedDate: PropTypes.string,
            requestedSubmittedDate: PropTypes.string,
            initiatedBy: PropTypes.string
        })
    }),
    loadingStatus: PropTypes.string,
    assetDetails: PropTypes.object,
    selectedAsset: PropTypes.object,
    actions: PropTypes.shape({
        removeSelectedKpiLegend: PropTypes.func,
        setIsZoomed: PropTypes.func,
        reloadConditionInsights: PropTypes.func,
        getRetrainingProgress: PropTypes.func
    }),
    title: PropTypes.string,
    levelLabel: PropTypes.string,
    isZoomed: PropTypes.bool
};

export default MotorAnomalyDetectionComponent;
